import { gql } from '@reverbdotcom/commons/src/gql';
import { compact, castArray } from 'lodash';
import { SellerInfoFragment } from '@reverbdotcom/commons/src/components/seller_info';
import { userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import { canViewFinancingOptions } from '@reverbdotcom/commons/src/user_context_helpers';
import {
  localizeItemRegionFilter,
  applyProximityBoost,
} from '@reverbdotcom/commons/src/seller_location_experiments';
import { CONDITION_GROUPS } from '@reverbdotcom/commons/src/condition_helpers';
import {
  reverb_search_ListingsSearchRequest_Aggregation as Aggregation,
  reverb_search_ListingsSearchRequest_Sort,
  CSP,
} from '@reverbdotcom/commons/src/gql/graphql';
import { getItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';

import {
  CSP_BUMP_LISTING_LIMIT,
  LISTINGS_LIMIT,
  LISTINGS_GRID_LIMIT,
  FRETBOARD_MATERIAL_TRAIT_UUID,
} from '../constants';
import { getOffsetFromPage } from '../../../pagination_helpers';
import toBool from '../../../reverb-js-common/to_boolean';
import { type ICSPLayoutProps } from '../csp_layout';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { ListingCardFragment } from '@reverbdotcom/commons/src/components/listing_card';

const CATEGORY_SLUGS_WITH_TRAITS = new Set([
  'electric-guitars',
]);

export function shouldRequestTraits(rootCategorySlug: string) {
  return CATEGORY_SLUGS_WITH_TRAITS.has(rootCategorySlug);
}

export function getRootCategorySlug(csp: CSP) {
  const [firstCategory] = csp?.categories || [];

  if (!firstCategory) { return undefined; }

  return firstCategory.root ? firstCategory.slug : firstCategory.rootSlug;
}

export function hideBumpedAndSponsoredListings({ pageNumber, sortQuery }) {
  return parseInt(pageNumber, 10) > 1 || !!sortQuery;
}

export const cspListingFragment = gql(`
  fragment ListingData on Listing {
    _id
    upc
    finish
    model
    shopUuid
    ...SellerInfoData
    ...ListingCardFields
  }
  ${SellerInfoFragment}
  ${ListingCardFragment}
`);

export const cspFiltersAndListingsQuery = gql(`
  query Csp_Filters_And_ListingsQuery(
    $acceptsAffirm: Boolean
    $acceptsGiftCards: Boolean
    $acceptsOffers: Boolean
    $acceptsPaymentPlans: Boolean
    $aggs: [reverb_search_ListingsSearchRequest_Aggregation]
    $applyProximityBoost: Boolean
    $boostedItemRegionCode: String
    $bumpLimit: Int
    $dealsAndSteals: Boolean
    $decades: [String]
    $canonicalFinishes: [String]
    $categorySlugs: [String]
    $combinedShipping: Boolean
    $conditionSlugs: [String]
    $cspSlug: String
    $excludeLocalPickupOnly: Boolean
    $fallbackToEverywhereElse: Boolean
    $freeExpeditedShipping: Boolean
    $freeShipping: Boolean
    $fretboardMaterial: String
    $includeFullFinancingFields: Boolean!
    $itemRegion: String
    $itemRegionRelation: reverb_search_ListingItemRegionRelation
    $limit: Int
    $localPickup: Boolean
    $offset: Int
    $onSale: Boolean
    $preferredSeller: Boolean
    $priceMin: String
    $priceMax: String
    $query: String
    $shippingRegionCode: String
    $shippingRegionCodes: [String]
    $shouldHideBumpAndSponsoredListings: Boolean!
    $shouldSkipTracking: Boolean!
    $sort: reverb_search_ListingsSearchRequest_Sort
    $sortSlug: String
    $sponsoredConditionSlugs: [String]
    $traitValues: [String]
    $withProximityFilter: Input_reverb_search_ProximityFilterRequest
    $yearMax: String
    $yearMin: String
    $userCurrency: String
  ) {
    allListings: listingsSearch(
      input: {
        applyProximityBoost: $applyProximityBoost
        boostedItemRegionCode: $boostedItemRegionCode
        cspSlug: $cspSlug
        conditionSlugs: $conditionSlugs
        statuses: ["live"]
        canonicalFinishes: $canonicalFinishes
        traitValues: $traitValues
        withAggregations: $aggs
        priceMin: $priceMin
        priceMax: $priceMax
        query: $query
        categorySlugs: $categorySlugs
        sort: $sort
        sortSlug: $sortSlug
        localPickup: $localPickup
        acceptsOffers: $acceptsOffers
        onSale: $onSale
        excludeLocalPickupOnly: $excludeLocalPickupOnly
        freeShipping: $freeShipping
        freeExpeditedShipping: $freeExpeditedShipping
        combinedShipping: $combinedShipping
        acceptsPaymentPlans: $acceptsPaymentPlans
        dealsAndSteals: $dealsAndSteals
        decades: $decades
        yearMax: $yearMax
        yearMin: $yearMin
        acceptsGiftCards: $acceptsGiftCards
        preferredSeller: $preferredSeller
        acceptsAffirm: $acceptsAffirm
        itemRegion: $itemRegion
        itemRegionRelation: $itemRegionRelation
        shippingRegionCodes: $shippingRegionCodes
        withProximityFilter: $withProximityFilter
        fallbackToEverywhereElse: $fallbackToEverywhereElse
        offset: $offset
        limit: $limit
      }
    ) {
      total
      offset
      filters {
        ...NestedFilter
      }
      listings {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... ListingGreatValueData
      }
      fallbackListings {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... ListingGreatValueData
      }
    }
    allBumped: bumpedSortedListings(
      input: {
        cspSlug: $cspSlug
        conditionSlugs: $conditionSlugs
        statuses: ["live"]
        sort: PRICE_ASC
        filterSuperRegionCode: $shippingRegionCode
        bumpedOnly: true
        canonicalFinishes: $canonicalFinishes
        traitValues: $traitValues
        withAggregations: $aggs
        priceMin: $priceMin
        priceMax: $priceMax
        query: $query
        categorySlugs: $categorySlugs
        localPickup: $localPickup
        acceptsOffers: $acceptsOffers
        onSale: $onSale
        freeShipping: $freeShipping
        freeExpeditedShipping: $freeExpeditedShipping
        combinedShipping: $combinedShipping
        acceptsPaymentPlans: $acceptsPaymentPlans
        dealsAndSteals: $dealsAndSteals
        decades: $decades
        yearMax: $yearMax
        yearMin: $yearMin
        acceptsGiftCards: $acceptsGiftCards
        preferredSeller: $preferredSeller
        acceptsAffirm: $acceptsAffirm
        shippingRegionCodes: $shippingRegionCodes
        withProximityFilter: $withProximityFilter
        currency: $userCurrency
        bumpedSortedListingsQuery: {
          condition: "all"
          bumpedMax: $bumpLimit
          total: $bumpLimit
          itemRegion: $itemRegion
        }
      }
    ) @skip (if: $shouldHideBumpAndSponsoredListings) {
      listings {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... BumpKey
        ... ListingGreatValueData
      }
    }
    cspSponsoredListing(input: {
      cspSlug: $cspSlug
      conditionSlugs: $sponsoredConditionSlugs
      statuses: ["live"]
      sort: PRICE_ASC
      canonicalFinishes: $canonicalFinishes
      traitValues: $traitValues
      priceMin: $priceMin
      priceMax: $priceMax
      query: $query
      categorySlugs: $categorySlugs
      localPickup: $localPickup
      acceptsOffers: $acceptsOffers
      onSale: $onSale
      freeShipping: $freeShipping
      freeExpeditedShipping: $freeExpeditedShipping
      combinedShipping: $combinedShipping
      acceptsPaymentPlans: $acceptsPaymentPlans
      dealsAndSteals: $dealsAndSteals
      decades: $decades
      yearMax: $yearMax
      yearMin: $yearMin
      acceptsGiftCards: $acceptsGiftCards
      preferredSeller: $preferredSeller
      acceptsAffirm: $acceptsAffirm
      shippingRegionCodes: $shippingRegionCodes
      withProximityFilter: $withProximityFilter
      itemRegion: $itemRegion
      itemRegionRelation: $itemRegionRelation
      currency: $userCurrency
    }) @skip (if: $shouldHideBumpAndSponsoredListings) {
      listing {
        _id
        ... ListingData
        ... ListingsCollection
        ... mParticleListingFields @skip(if: $shouldSkipTracking)
        ... FullFinancingFields @include(if: $includeFullFinancingFields)
        ... BumpKey
        ... ListingGreatValueData
      }
      runnerUp {
        listing {
          _id
          ... ListingData
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
          ... FullFinancingFields @include(if: $includeFullFinancingFields)
          ... BumpKey
          ... ListingGreatValueData
        }
        runnerUp {
          listing {
            _id
            ... ListingData
            ... ListingsCollection
            ... mParticleListingFields @skip(if: $shouldSkipTracking)
            ... FullFinancingFields @include(if: $includeFullFinancingFields)
            ... BumpKey
            ... ListingGreatValueData
          }
        }
      }
    }
    finishAgg: listingsAggregationSearch(input: {
      withAggregations: CANONICAL_FINISH
      cspSlug: $cspSlug
      traitValues: $traitValues
    }) {
      aggregationResults {
        name
        aggregationDetails {
          key
          count
        }
      }
    }
    fretboardMaterialAgg: listingsAggregationSearch(input: {
      canonicalFinishes: $canonicalFinishes
      withAggregations: TRAITS
      cspSlug: $cspSlug
      traitValues: [$fretboardMaterial]
    }) {
      aggregationResults {
        name
        displayName
        aggregationDetails {
          key
          displayName
          count
        }
      }
    }
  }
`);

export const getFiltersAndListingsVariables = ({
  location,
  params,
  user,
  rootCategorySlug,
}: ICSPLayoutProps & { rootCategorySlug?: string } & { user: Partial<IUser> }) => {
  const queryParams = location?.query || {};
  const isGridEnabled = !getItem(LIST_VIEW_STATE);
  const limit = isGridEnabled ? LISTINGS_GRID_LIMIT : LISTINGS_LIMIT;
  const categorySlugs = shouldRequestTraits(rootCategorySlug) ? compact(castArray(rootCategorySlug)) : undefined;
  const aggs = [
    Aggregation.CONDITION_SLUGS,
    Aggregation.DECADES,
  ];

  if (shouldRequestTraits(rootCategorySlug)) {
    aggs.push(Aggregation.TRAITS);
  }

  function shippingRegionCodes() {
    if (queryParams.ships_to) {
      return compact([queryParams.ships_to]);
    }

    if (user?.shippingRegionCode) {
      return compact([user?.shippingRegionCode]);
    }

    return null;
  }

  return {
    acceptsAffirm: queryParams.accepts_affirm,
    acceptsGiftCards: queryParams.accepts_gift_cards,
    acceptsOffers: queryParams.accepts_offers,
    acceptsPaymentPlans: queryParams.zero_percent_financing,
    aggs,
    applyProximityBoost: applyProximityBoost(user),
    boostedItemRegionCode: user?.countryCode || '',
    bumpLimit: CSP_BUMP_LISTING_LIMIT + 1,
    canonicalFinishes: compact([queryParams.canonicalFinish]),
    categorySlugs,
    combinedShipping: queryParams.combined_shipping,
    conditionSlugs: compact(castArray(queryParams.condition)),
    cspSlug: params.slug,
    dealsAndSteals: queryParams.deals_and_steals,
    decades: queryParams.decades,
    excludeLocalPickupOnly: queryParams.exclude_local_pickup_only,
    fallbackToEverywhereElse: applyProximityBoost(user),
    freeExpeditedShipping: queryParams.free_expedited_shipping,
    freeShipping: queryParams.free_shipping,
    fretboardMaterial: FRETBOARD_MATERIAL_TRAIT_UUID,
    includeFullFinancingFields: canViewFinancingOptions(user),
    limit,
    ...localizeItemRegionFilter(location?.query?.item_region, user),
    localPickup: queryParams.local_pickup,
    offset: getOffsetFromPage(
      queryParams.page,
      limit,
    ),
    onSale: queryParams.on_sale,
    preferredSeller: queryParams.preferred_seller,
    priceMin: location.query.price_min,
    priceMax: location.query.price_max,
    query: queryParams.query,
    shippingRegionCode: user?.shippingRegionCode,
    shippingRegionCodes: shippingRegionCodes(),
    shouldHideBumpAndSponsoredListings: hideBumpedAndSponsoredListings({
      pageNumber: queryParams.page,
      sortQuery: queryParams.sort,
    }),
    shouldSkipTracking: !userContextIsTrackable(user),
    sort: reverb_search_ListingsSearchRequest_Sort.NONE,
    sortSlug: queryParams.sort,
    sponsoredConditionSlugs: queryParams.condition ? compact(castArray(queryParams.condition)) : [CONDITION_GROUPS.new],
    traitValues: compact([(queryParams.trait_values || '')]).flat(),
    userCurrency: user?.currency,
    yearMin: queryParams.year_min,
    yearMax: queryParams.year_max,
    withProximityFilter: {
      proximity: toBool(queryParams.proximity),
      distance: queryParams.distance,
      postalCode: queryParams.postal_code,
    },
  };
};
